import { useEffect, useState } from 'react'

// Check browser support for the Web Speech API
const isSupported = (): boolean => {
  return (
    'SpeechRecognition' in window ||
    'webkitSpeechRecognition' in window ||
    'SpeechSynthesis' in window
  )
}

// Define interfaces for options
interface SpeechRecognitionOptions {
  continuous?: boolean
  language?: string
  onError?: (event: any) => void
}

interface SpeakOptions {
  voice?: SpeechSynthesisVoice
  lang?: string
  onEnd?: () => void // New optional callback for when speaking ends
}

// Custom hook for Speech Recognition and Speech Synthesis
const useSpeech = () => {
  const [transcript, setTranscript] = useState<string>('')
  const [isListening, setIsListening] = useState<boolean>(false)
  const [speechSupported, setSpeechSupported] = useState<boolean>()
  const [isMicrophoneAvailable, setIsMicrophoneAvailable] = useState<boolean>(false)
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  //@ts-ignore
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null)

  useEffect(() => {
    setSpeechSupported(isSupported())
  }, [])

  useEffect(() => {
    const handleVoicesChange = () => {
      const speechSynthesis = window.speechSynthesis
      const voiceList = speechSynthesis.getVoices()
      const engVoices = voiceList.filter((voice) => voice.lang.startsWith('en-'))
      setVoices(engVoices || voiceList) // Set voices to English voices or all available voices
    }

    if (isMicrophoneAvailable && speechSupported) {
      handleVoicesChange() // Load voices initially
      window.speechSynthesis.onvoiceschanged = handleVoicesChange // Update voices when changed
    }

    return () => {
      window.speechSynthesis.onvoiceschanged = null // Cleanup event listener
    }
  }, [isMicrophoneAvailable])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel() // Stop any ongoing speech synthesis
      }
    }

    // Add event listener for browser refresh or tab close
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      // Cleanup event listener
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel() // Stop any ongoing speech synthesis
      }
    }
  }, [])

  const checkSpeechAccess = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true })
      setIsMicrophoneAvailable(true)
      return true
    } catch {
      setIsMicrophoneAvailable(false)
      return false
    }
  }

  const startListening = (options?: SpeechRecognitionOptions): void => {
    if (!speechSupported) return
    //@ts-ignore
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition
    const newRecognition = new SpeechRecognition()
    setRecognition(newRecognition)

    if (options) {
      newRecognition.continuous = options.continuous ?? false
      newRecognition.lang = options.language ?? 'en-GB'
      newRecognition.interimResults = true
      newRecognition.onerror = options.onError ?? (() => {})
    }

    newRecognition.onstart = () => setIsListening(true)
    //@ts-ignore
    newRecognition.onresult = (event: SpeechRecognitionEvent) => {
      let currentTranscript = ''
      for (let i = 0; i < event.results.length; i++) {
        currentTranscript += `${event.results[i][0].transcript} `
      }
      setTranscript(currentTranscript.trim())
    }

    newRecognition.onend = () => {
      setIsListening(false)
    }

    newRecognition.start()
  }

  const stopListening = (): void => {
    if (recognition) {
      recognition.stop()
      setIsListening(false)
    }
  }

  const resetTranscript = (): void => setTranscript('')

  const speak = (text: string, options?: SpeakOptions) => {
    if (!speechSupported) return

    const utterance = new SpeechSynthesisUtterance(text)
    if (options) {
      if (options.voice) utterance.voice = options.voice
      if (options.lang) utterance.lang = options.lang
      if (options.onEnd) utterance.onend = options.onEnd // Connect onEnd
    }
    // utterance.onerror = (event) => console.log('Speech synthesis on error:', event)

    window.speechSynthesis.speak(utterance)
  }

  const stopSpeaking = (): void => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel() // Stop any ongoing speech
    }
  }

  return {
    transcript,
    isListening,
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    resetTranscript,
    browserSupportsSpeech: speechSupported,
    isMicrophoneAvailable,
    voices,
    checkSpeechAccess
  }
}

export default useSpeech
